<template>
  <div class="visualization-axes">
    <!-- Add Axis Button -->
    <div class="axes-header">
      <button type="button" class="btn btn-primary" @click="openAddModal">
        <span class="material-symbols-outlined">add</span>
        Ajouter un axe
      </button>
    </div>

    <!-- Empty State -->
    <div class="axes-empty" v-if="!axes || axes.length === 0">
      <span class="material-symbols-outlined">ssid_chart</span>
      <h4>Aucun axe configuré</h4>
      <p>Ajoutez des axes pour définir les mesures à afficher sur le graphique</p>
    </div>

    <!-- Axes Table -->
    <div class="axes-table-container" v-else>
      <table class="axes-table">
        <thead>
        <tr>
          <th class="col-color">Couleur</th>
          <th class="col-type">Type</th>
          <th class="col-measures">Mesures</th>
          <th class="col-agg">Agrégation</th>
          <th class="col-minmax">Min</th>
          <th class="col-minmax">Max</th>
          <th class="col-toggle">Afficher</th>
          <th class="col-toggle">Min/Max</th>
          <th class="col-toggle">Global Min/Max</th>
          <th class="col-actions"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(axe, index) in axes" :key="index" class="axis-row">
          <!-- Color -->
          <td class="col-color">
            <v-menu :close-on-content-click="false" offset-y nudge-bottom="5">
              <template v-slot:activator="{ on, attrs }">
                <div class="color-trigger" v-bind="attrs" v-on="on">
                  <div
                      class="color-swatch"
                      :style="{ backgroundColor: getColorValue(axe.color) }"
                  >
                    <span class="material-symbols-outlined edit-icon">edit</span>
                  </div>
                </div>
              </template>
              <div class="color-picker-dropdown">
                <v-color-picker
                    v-model="axe.color.hex"
                    mode="hexa"
                    show-swatches
                    swatches-max-height="150"
                ></v-color-picker>
              </div>
            </v-menu>
          </td>

          <!-- Type -->
          <td class="col-type">
            <v-select
                :items="axeTypes"
                v-model="axe.type"
                item-text="text"
                item-value="value"
                solo
                flat
                hide-details
                dense
                class="compact-select"
            ></v-select>
          </td>

          <!-- Measures -->
          <td class="col-measures">
            <v-autocomplete
                v-model="axe.mesures"
                :items="mesures"
                :item-text="getMesureText"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                return-object
                solo
                flat
                hide-details
                dense
                placeholder="Sélectionner..."
                class="compact-autocomplete"
            ></v-autocomplete>
          </td>

          <!-- Aggregation -->
          <td class="col-agg">
            <v-select
                :items="axeAggs"
                v-model="axe.aggregationsType"
                solo
                flat
                hide-details
                dense
                class="compact-select"
            ></v-select>
          </td>

          <!-- Min -->
          <td class="col-minmax">
            <input
                type="number"
                v-model="axe.min"
                class="minmax-input"
                placeholder="Auto"
            />
          </td>

          <!-- Max -->
          <td class="col-minmax">
            <input
                type="number"
                v-model="axe.max"
                class="minmax-input"
                placeholder="Auto"
            />
          </td>

          <!-- Display Toggle -->
          <td class="col-toggle">
            <label class="toggle-checkbox">
              <input type="checkbox" v-model="axe.display" />
              <span class="toggle-switch"></span>
            </label>
          </td>

          <!-- Min/Max Toggle -->
          <td class="col-toggle">
            <label class="toggle-checkbox">
              <input type="checkbox" v-model="axe.allowMinMaxAxes" />
              <span class="toggle-switch"></span>
            </label>
          </td>

          <!-- Min/Max Toggle -->
          <td class="col-toggle">
            <label class="toggle-checkbox">
              <input type="checkbox" v-model="axe.useGlobalMinMax" />
              <span class="toggle-switch"></span>
            </label>
          </td>

          <!-- Actions -->
          <td class="col-actions">
            <button
                type="button"
                class="delete-btn"
                @click="deleteAxe(axe)"
                title="Supprimer"
            >
              <span class="material-symbols-outlined">delete</span>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Axis Modal -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="axeModal" @click.self="closeModal">
        <div class="modal-card">
          <div class="modal-header">
            <h3>
              <span class="material-symbols-outlined">add_chart</span>
              Ajouter un axe
            </h3>
            <button type="button" class="modal-close" @click="closeModal">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="modal-body">
            <!-- Color Picker - Same style as table -->
            <div class="modal-field">
              <label>Couleur de l'axe</label>
              <v-menu :close-on-content-click="false" offset-y nudge-bottom="5">
                <template v-slot:activator="{ on, attrs }">
                  <div class="color-selector-large" v-bind="attrs" v-on="on">
                    <div
                        class="color-swatch-large"
                        :style="{ backgroundColor: getColorValue(axeCreated.color) }"
                    >
                      <span class="material-symbols-outlined">edit</span>
                    </div>
                    <div class="color-info">
                      <span class="color-label">Cliquez pour modifier</span>
                      <span class="color-value">{{ getColorValue(axeCreated.color) }}</span>
                    </div>
                    <span class="material-symbols-outlined chevron">expand_more</span>
                  </div>
                </template>
                <div class="color-picker-dropdown">
                  <v-color-picker
                      v-model="axeCreated.color.hex"
                      mode="hexa"
                      show-swatches
                      swatches-max-height="150"
                  ></v-color-picker>
                </div>
              </v-menu>
            </div>

            <!-- Type -->
            <div class="modal-field">
              <label>Type de graphique</label>
              <div class="type-selector">
                <button
                    v-for="typeOption in axeTypes"
                    :key="typeOption.value"
                    type="button"
                    class="type-btn"
                    :class="{ active: axeCreated.type === typeOption.value }"
                    @click="axeCreated.type = typeOption.value"
                >
                  <span class="material-symbols-outlined">{{ getTypeIcon(typeOption.value) }}</span>
                  {{ typeOption.text }}
                </button>
              </div>
            </div>

            <!-- Measures -->
            <div class="modal-field">
              <label>Mesures à inclure</label>
              <v-autocomplete
                  v-model="axeCreated.mesures"
                  :items="mesures"
                  :item-text="getMesureText"
                  item-value="id"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  return-object
                  solo
                  flat
                  hide-details
                  dense
                  placeholder="Sélectionner des mesures..."
                  class="modal-autocomplete"
              ></v-autocomplete>
            </div>

            <!-- Aggregation -->
            <div class="modal-field">
              <label>Type d'agrégation</label>
              <v-select
                  :items="axeAggs"
                  v-model="axeCreated.aggregationsType"
                  solo
                  flat
                  hide-details
                  dense
                  placeholder="Sélectionner..."
                  class="modal-select"
              ></v-select>
            </div>

            <!-- Min/Max -->
            <div class="modal-field-row">
              <div class="modal-field">
                <label>Valeur minimum</label>
                <input
                    type="number"
                    v-model="axeCreated.min"
                    class="form-input"
                    placeholder="Auto"
                />
              </div>
              <div class="modal-field">
                <label>Valeur maximum</label>
                <input
                    type="number"
                    v-model="axeCreated.max"
                    class="form-input"
                    placeholder="Auto"
                />
              </div>
            </div>

            <!-- Options -->
            <div class="modal-field">
              <label>Options d'affichage</label>
              <div class="options-row">
                <label class="option-toggle" :class="{ active: axeCreated.display }">
                  <input type="checkbox" v-model="axeCreated.display" />
                  <span class="toggle-switch"></span>
                  <span class="option-text">
                    <span class="material-symbols-outlined">visibility</span>
                    Afficher l'axe
                  </span>
                </label>
                <label class="option-toggle" :class="{ active: axeCreated.allowMinMaxAxes }">
                  <input type="checkbox" v-model="axeCreated.allowMinMaxAxes" />
                  <span class="toggle-switch"></span>
                  <span class="option-text">
                    <span class="material-symbols-outlined">unfold_more</span>
                    Afficher Min/Max
                  </span>
                </label>
                <label class="option-toggle" :class="{ active: axeCreated.useGlobalMinMax }">
                  <input type="checkbox" v-model="axeCreated.useGlobalMinMax" />
                  <span class="toggle-switch"></span>
                  <span class="option-text">
                    <span class="material-symbols-outlined">unfold_more</span>
                    Utiliser Min/Max global
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-ghost" @click="closeModal">
              Annuler
            </button>
            <button type="button" class="btn btn-primary" @click="handleAddedAxe">
              <span class="material-symbols-outlined">add</span>
              Ajouter l'axe
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'VisualizationAxes',

  props: {
    axes: {
      type: Array,
      default: () => [],
    },
    mesures: {
      type: Array,
      default: () => [],
    },
    site: {
      type: Object,
      default: () => ({}),
    },
    childIdsToName: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      axeTypes: [
        { text: "Bars", value: "Bars" },
        { text: "Lignes", value: "Lignes" },
        { text: "Histogram", value: "Histogram" },
        { text: "Area", value: "AreaChart" },
        { text: "Donut", value: "DonutChart" },
        { text: "Pie", value: "PieChart" },
        { text: "Polar Area", value: "PolarAreaChart" },
        { text: "Scatter", value: "ScatterChart" },
        { text: "Bubble", value: "BubbleChart" },
      ],
      axeAggs: ["Moyenne", "Cumul", "Maximum", "Min/Max", "Delta"],
      axeModal: false,
      axeCreated: {
        display: true,
        allowMinMaxAxes: false,
        useGlobalMinMax: false,
        color: {hex: '#5a9e6f'},
        type: 'Lignes',
        mesures: [],
        aggregationsType: 'Moyenne'
      },
    };
  },

  mounted() {
    (Array.isArray(this.axes) ? this.axes : []).forEach(axe => {
      if (axe.display === undefined) {
        axe.display = true;
      }
      if (axe.type) {
        axe.type = this.normalizeAxeType(axe.type);
      }
    });
  },

  methods: {
    normalizeAxeType(type) {
      if (!type) return "Lignes";
      const t = String(type);
      if (t === "BarChart") return "Bars";
      if (t === "LineChart") return "Lignes";
      if (t === "RadarChart") return "Radar";
      if (t === "HistogramChart") return "Histogram";
      return t;
    },
    getTypeIcon(type) {
      switch (type) {
        case "Bars":
          return "bar_chart";
        case "Lignes":
          return "show_chart";
        case "Histogram":
          return "stacked_bar_chart";
        case "Radar":
          return "radar";
        case "AreaChart":
          return "multiline_chart";
        case "DonutChart":
          return "donut_large";
        case "PieChart":
          return "pie_chart";
        case "PolarAreaChart":
          return "donut_small";
        case "ScatterChart":
          return "scatter_plot";
        case "BubbleChart":
          return "bubble_chart";
        default:
          return "show_chart";
      }
    },
    getColorValue(color) {
      if (!color) return '#5a9e6f';
      if (typeof color === 'object' && color.hex) {
        return color.hex;
      }
      return color;
    },

    getMesureText(item) {
      let name = "";
      if (item.site_id !== this.site.id && this.childIdsToName) {
        const childName = this.childIdsToName[item.site_id];
        if (childName) {
          name += childName + " : ";
        }
      }
      name += item.name;
      return name;
    },

    openAddModal() {
      this.axeCreated = {
        display: true,
        allowMinMaxAxes: false,
        useGlobalMinMax: false,
        color: {hex: this.getNextColor()},
        type: 'Lignes',
        mesures: [],
        aggregationsType: 'Moyenne'
      };
      this.axeModal = true;
    },

    closeModal() {
      this.axeModal = false;
    },

    getNextColor() {
      const colors = [
        '#5a9e6f', '#5B9BD5', '#FF6B6B', '#FFE66D',
        '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA',
        '#2d5a3d', '#d97706', '#dc2626', '#7c3aed'
      ];
      const length = Array.isArray(this.axes) ? this.axes.length : 0;
      return colors[length % colors.length];
    },

    handleAddedAxe() {
      const newAxe = {
        ...this.axeCreated,
        color: {hex: this.getColorValue(this.axeCreated.color)}
      };
      this.axes.push(newAxe);
      this.axeModal = false;
      this.$emit("added", newAxe);
    },

    deleteAxe(axe) {
      if (confirm("Êtes-vous sûr de vouloir supprimer cet axe ?")) {
        const index = this.axes.indexOf(axe);
        if (index > -1) {
          this.axes.splice(index, 1);
        }
        this.$emit("deleted", axe);
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.visualization-axes {
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-bg: #e8f0eb;

  --color-accent: #5a9e6f;

  --color-surface: #ffffff;
  --color-surface-secondary: #f8faf9;
  --color-surface-tertiary: #f0f4f2;
  --color-surface-hover: #eef2ef;

  --color-border: #dce5df;
  --color-border-light: #e8eeea;

  --color-text-primary: #1a2e1f;
  --color-text-secondary: #4a5d4f;
  --color-text-muted: #7a8d7f;

  --color-danger: #dc2626;
  --color-danger-bg: #fef2f2;

  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.15);

  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;

  --transition-fast: 150ms ease;
  --transition-normal: 200ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.axes-header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.axes-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px;
  background: var(--color-surface-secondary);
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-md);
}

.axes-empty .material-symbols-outlined {
  font-size: 36px;
  color: var(--color-text-muted);
  opacity: 0.4;
  margin-bottom: 10px;
}

.axes-empty h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

.axes-empty p {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* ============================================
   TABLE
   ============================================ */
.axes-table-container {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  overflow-x: auto;
}

.axes-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.axes-table thead {
  background: var(--color-surface-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.axes-table th {
  padding: 12px 14px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  text-align: left;
  white-space: nowrap;
}

.axes-table td {
  padding: 12px 14px;
  vertical-align: middle;
  border-bottom: 1px solid var(--color-border-light);
}

.axes-table tbody tr:last-child td {
  border-bottom: none;
}

.axis-row {
  transition: var(--transition-fast);
}

.axis-row:hover {
  background: var(--color-surface-secondary);
}

/* Column widths */
.col-color { width: 70px; }
.col-type { width: 120px; }
/*.col-measures { min-width: 200px; }*/
.col-measures { width: 150px; }
.col-agg { width: 130px; }
.col-minmax { width: 90px; }
.col-toggle { width: 50px; text-align: center; }
.col-actions { width: 50px; text-align: right; }

/* ============================================
   COLOR TRIGGER (Table)
   ============================================ */
.color-trigger {
  display: inline-block;
  cursor: pointer;
}

.color-swatch {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  border: 2px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-normal);
  cursor: pointer;
}

.color-swatch:hover {
  transform: scale(1.08);
  box-shadow: var(--shadow-md);
}

.color-swatch .edit-icon {
  font-size: 16px;
  color: white;
  opacity: 0;
  transition: var(--transition-fast);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.color-swatch:hover .edit-icon {
  opacity: 1;
}

.color-picker-dropdown {
  background: var(--color-surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

/* ============================================
   MINMAX INPUT
   ============================================ */
.minmax-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  color: var(--color-text-primary);
  background: var(--color-surface);
  transition: var(--transition-normal);
  font-family: 'SF Mono', monospace;
  text-align: right;
}

.minmax-input:hover {
  border-color: var(--color-primary-lighter);
}

.minmax-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-bg);
}

.minmax-input::placeholder {
  color: var(--color-text-muted);
  font-family: inherit;
}

/* ============================================
   TOGGLE CHECKBOX
   ============================================ */
.toggle-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.toggle-checkbox input {
  display: none;
}

.toggle-switch {
  width: 38px;
  height: 22px;
  background: var(--color-border);
  border-radius: 11px;
  position: relative;
  transition: var(--transition-normal);
}

.toggle-switch::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
}

.toggle-checkbox input:checked + .toggle-switch {
  background: var(--color-primary);
}

.toggle-checkbox input:checked + .toggle-switch::after {
  transform: translateX(16px);
}

/* ============================================
   DELETE BUTTON
   ============================================ */
.delete-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
}

.delete-btn:hover {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.delete-btn .material-symbols-outlined {
  font-size: 20px;
}

/* ============================================
   MODAL
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}

.modal-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: var(--color-surface-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.modal-header h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.modal-header h3 .material-symbols-outlined {
  font-size: 22px;
  color: var(--color-accent);
}

.modal-close {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
}

.modal-close:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-field label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.modal-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

/* ============================================
   COLOR SELECTOR (Modal - Large)
   ============================================ */
.color-selector-large {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
}

.color-selector-large:hover {
  border-color: var(--color-primary-lighter);
  background: var(--color-surface-hover);
}

.color-swatch-large {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-sm);
  border: 2px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: var(--transition-normal);
}

.color-swatch-large .material-symbols-outlined {
  font-size: 20px;
  color: white;
  opacity: 0;
  transition: var(--transition-fast);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.color-selector-large:hover .color-swatch-large .material-symbols-outlined {
  opacity: 1;
}

.color-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.color-label {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.color-value {
  font-size: 0.75rem;
  font-family: 'SF Mono', monospace;
  color: var(--color-text-muted);
}

.color-selector-large .chevron {
  font-size: 20px;
  color: var(--color-text-muted);
  transition: var(--transition-fast);
}

.color-selector-large:hover .chevron {
  color: var(--color-text-secondary);
}

/* ============================================
   TYPE SELECTOR
   ============================================ */
.type-selector {
  display: flex;
  gap: 10px;
}

.type-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.type-btn:hover {
  border-color: var(--color-primary-lighter);
  background: var(--color-surface-hover);
}

.type-btn.active {
  background: var(--color-primary-bg);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.type-btn .material-symbols-outlined {
  font-size: 20px;
}

/* ============================================
   FORM INPUT
   ============================================ */
.form-input {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  color: var(--color-text-primary);
  background: var(--color-surface);
  transition: var(--transition-normal);
  font-family: inherit;
}

.form-input:hover {
  border-color: var(--color-primary-lighter);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.form-input::placeholder {
  color: var(--color-text-muted);
}

/* ============================================
   OPTIONS ROW
   ============================================ */
.options-row {
  display: flex;
  gap: 12px;
}

.option-toggle {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-normal);
}

.option-toggle:hover {
  border-color: var(--color-primary-lighter);
}

.option-toggle.active {
  background: var(--color-primary-bg);
  border-color: var(--color-primary);
}

.option-toggle input {
  display: none;
}

.option-toggle .toggle-switch {
  width: 36px;
  height: 20px;
  flex-shrink: 0;
}

.option-toggle .toggle-switch::after {
  width: 16px;
  height: 16px;
}

.option-toggle.active .toggle-switch {
  background: var(--color-primary);
}

.option-toggle.active .toggle-switch::after {
  transform: translateX(16px);
}

.option-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.option-text .material-symbols-outlined {
  font-size: 18px;
}

.option-toggle.active .option-text {
  color: var(--color-primary);
}

/* ============================================
   MODAL FOOTER
   ============================================ */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  background: var(--color-surface-secondary);
  border-top: 1px solid var(--color-border-light);
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-light);
}

.btn-ghost {
  background: transparent;
  color: var(--color-text-secondary);
}

.btn-ghost:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.compact-select >>> .v-input__slot,
.compact-autocomplete >>> .v-input__slot {
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 38px !important;
  padding: 0 10px !important;
}

.compact-select >>> .v-input__slot:hover,
.compact-autocomplete >>> .v-input__slot:hover {
  border-color: var(--color-primary-lighter) !important;
}

.compact-autocomplete >>> .v-chip {
  background: var(--color-primary) !important;
  color: white !important;
  height: 24px !important;
  font-size: 0.75rem !important;
}

.modal-select >>> .v-input__slot,
.modal-autocomplete >>> .v-input__slot {
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 44px !important;
}

.modal-select >>> .v-input__slot:hover,
.modal-autocomplete >>> .v-input__slot:hover {
  border-color: var(--color-primary-lighter) !important;
}

.modal-autocomplete >>> .v-chip {
  background: var(--color-primary) !important;
  color: white !important;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-card {
  animation: modal-in 0.25s ease-out;
}

.modal-fade-leave-active .modal-card {
  animation: modal-in 0.2s ease-in reverse;
}

@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>
